import React, { useState, useEffect } from "react";
import axios from "../../api/axios";
import PeopleCellLoader from "../common/people/PeopleCellLoader";
import { viewApprovalLine, addApprovalLine } from "../../api/approval";
import { updateDocStatus } from "../../api/papers";

export default function DocApprovalDualTable({ dns }) {
    const me = JSON.parse(localStorage.getItem("user") || "{}");
    const myId = me.id;
    const projectId = localStorage.getItem("projectId");

    const [lines, setLines] = useState([]);
    const [myLine, setMyLine] = useState(null);

    const [cmComment, setCmComment] = useState("");
    const [ownerComment, setOwnerComment] = useState("");

    const [decision, setDecision] = useState(""); // approved / rejected
    const [nextAssignee, setNextAssignee] = useState([]); // 선택된 발주처 사용자


    /* -----------------------------------------------
       결재선 불러오기
    ------------------------------------------------ */
    useEffect(() => {
        const load = async () => {
            const res = await viewApprovalLine(dns);
            const l = res.data?.approval_lines || [];
            setLines(l);

            const submitOrders = l.filter(x => x.is_approved === "submit").map(x => x.order);
            const minOrder = Math.min(...submitOrders);

            const mine = l.find(x => x.approver_id === myId && x.order === minOrder);
            setMyLine(mine);

            const cmLine = l.find(x => x.order === 1);
            const ownerLine = l.find(x => x.order === 2);

            if (cmLine?.comment) setCmComment(cmLine.comment);
            if (ownerLine?.comment) setOwnerComment(ownerLine.comment);
        };

        load();
    }, [dns, myId]);
    /* -----------------------------------------------
       저장
    ------------------------------------------------ */
    const handleSave = async () => {
        if (!myLine) return;

        // 반려 시 의견 필수
        if (decision === "rejected") {
            const comment = myLine.order === 1 ? cmComment : ownerComment;
            if (!comment.trim()) return alert("반려 시 의견은 필수입니다.");
        }

        try {
            /* ----------------------------
               1️⃣ 내 결재 처리 (승인/반려)
            ---------------------------- */
            await axios.patch(`approval/action/${myLine.id}/`, {
                action: decision,
                comment: myLine.order === 1 ? cmComment : ownerComment,
            });

            /* ----------------------------
               2️⃣ CM (order === 1)
            ---------------------------- */
            if (myLine.order === 1 && decision === "approved") {
                const next = nextAssignee[0];
                const approverId = next?.user?.id || next?.id;

                if (!approverId) {
                    // 🔸 발주처 지정이 없을 때 — 종결 여부 확인
                    const confirmClose = window.confirm(
                        "발주처 승인 없이 문서를 종결하시겠습니까?"
                    );

                    if (confirmClose) {
                        await updateDocStatus(dns, "approved"); // ✅ 종결
                        alert("문서가 CM 승인으로 종결되었습니다.");
                        window.location.reload();
                        return;
                    } else {
                        alert("다음 담당자(발주처)를 지정해주세요.");
                        return;
                    }
                }

                // 🔹 정상 경로: 발주처 결재자 추가
                await addApprovalLine(
                    {
                        doc_dns: dns,
                        approver_id: approverId,
                        order: 2,
                    },
                    projectId
                );

                // ✅ 문서 상태를 'in_progress'로 변경 (발주처 결재 대기 중)
                await updateDocStatus(dns, "in_progress");
            }

            /* ----------------------------
               3️⃣ 발주처 (order === 2)
            ---------------------------- */
            if (myLine.order === 2) {
                if (decision === "approved") {
                    await updateDocStatus(dns, "approved"); // ✅ 발주처 승인 완료
                    alert("문서가 최종 승인되었습니다.");
                } else if (decision === "rejected") {
                    await updateDocStatus(dns, "rejected"); // ❌ 발주처 반려
                    alert("문서가 반려되었습니다.");
                }
                window.location.reload();
                return;
            }

            /* ----------------------------
               4️⃣ 반려 시 (CM or 발주처 공통)
            ---------------------------- */
            if (decision === "rejected") {
                await updateDocStatus(dns, "rejected");
            }

            alert("저장되었습니다.");
            window.location.reload();
        } catch (err) {
            console.error(err);
            alert("결재 처리 중 오류가 발생했습니다.");
        }
    };

    /* -----------------------------------------------
       UI
    ------------------------------------------------ */
    return (
        <div className="mt-3 text-xs">
            {/* 의견 테이블 */}
            <table className="w-full border table-fixed border-black">
                <colgroup>
                    <col style={{ width: "50%" }} />
                    <col style={{ width: "50%" }} />
                </colgroup>

                <thead className="doc-thead">
                    <tr className="bg-gray-100 border-black">
                        <th className="doc-th text-center">
                            CM사 의견 <br />
                            {/* <span className="text-xs">CM/CS Comments</span> */}
                        </th>
                        <th className="doc-th text-center">
                            발주처 의견 <br />
                            {/* <span className="text-xs">Client Comments</span> */}
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr>
                        {/* CM 의견 */}
                        <td className="border border-black p-2">
                            {myLine?.order === 1 ? (
                                <textarea
                                    className="w-full border rounded p-2 h-32"
                                    value={cmComment}
                                    onChange={(e) => setCmComment(e.target.value)}
                                />
                            ) : (
                                <div className="min-h-[120px] whitespace-pre-wrap">
                                    {cmComment || "-"}
                                </div>
                            )}
                        </td>

                        {/* 발주처 의견 */}
                        <td className="border border-black p-2">
                            {myLine?.order === 2 ? (
                                <textarea
                                    className="w-full border rounded p-2 h-32"
                                    value={ownerComment}
                                    onChange={(e) => setOwnerComment(e.target.value)}
                                />
                            ) : (
                                <div className="min-h-[120px] whitespace-pre-wrap">
                                    {ownerComment || "-"}
                                </div>
                            )}
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* 승인/반려 영역 */}
            {myLine && (
                <div className="mt-6 p-4 border-2 border-gray-200 rounded-lg bg-white shadow-sm">
                    <div className="mb-3">
                        <h4 className="text-sm font-bold text-gray-800">
                            결재 처리
                        </h4>
                        <p className="text-xs text-gray-600 mt-1">
                            승인 또는 반려를 선택하고 의견을 입력해주세요.
                        </p>
                    </div>

                    {/* 승인/반려 선택 버튼 */}
                    <div className="flex gap-3 mb-4">
                        <button
                            type="button"
                            onClick={() => setDecision("approved")}
                            className={`flex-1 py-2 text-sm font-semibold rounded-lg border transition-all ${decision === "approved"
                                    ? "bg-blue-100 text-blue-700 border-blue-400"
                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                }`}
                        >
                            승인
                        </button>

                        <button
                            type="button"
                            onClick={() => setDecision("rejected")}
                            className={`flex-1 py-2 text-sm font-semibold rounded-lg border transition-all ${decision === "rejected"
                                    ? "bg-red-100 text-red-600 border-red-400"
                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                }`}
                        >
                            반려
                        </button>
                    </div>

                    {/* 반려 시 경고 메시지 */}
                    {decision === "rejected" && (
                        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-xs font-semibold text-red-800">
                                ⚠ 반려 시 의견 입력은 필수입니다
                            </p>
                            <p className="text-xs text-red-600 mt-1">
                                반려 사유를 명확하게 입력해주세요.
                            </p>
                        </div>
                    )}

                    {/* CM 승인일 때 → 발주처 선택 */}
                    {myLine.order === 1 && decision === "approved" && (
                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <label className="text-sm font-semibold text-blue-800 mb-2 block">
                                다음 담당자 지정 (발주처)
                            </label>
                            <PeopleCellLoader value={nextAssignee} onChange={setNextAssignee} single />
                            <p className="text-xs text-blue-700 mt-2">
                                💡 발주처를 지정하지 않으면 CM 승인으로 문서가 종결됩니다.
                            </p>
                        </div>
                    )}

                    {/* 저장 버튼 */}
                    <button
                        type="button"
                        className={`w-full py-2 px-4 rounded-lg font-semibold text-sm transition-all ${!decision
                                ? "bg-gray-200 text-gray-500 cursor-not-allowed border border-gray-300"
                                : decision === "approved"
                                    ? "btn-save"
                                    : "btn-delete"
                            }`}
                        disabled={!decision}
                        onClick={handleSave}
                    >
                        {!decision
                            ? "승인 또는 반려를 선택하세요"
                            : decision === "approved"
                                ? "승인 처리하기"
                                : "반려 처리하기"}
                    </button>
                </div>
            )}
        </div>
    );
}
